import styled from "@emotion/styled";
import { Trans } from "@lingui/react/macro";
import orderBy from "lodash/orderBy";
import Router, { useRouter } from "next/router";
import { Dispatch, useMemo, useState } from "react";

import { PublicQuestion, TestType } from "@/queries/types";
import { faClose } from "@fortawesome/pro-regular-svg-icons";
import { useScrollToTop } from "../../utils";
import { Icon } from "../icon";
import { Timer } from "../timer";
import { ActiveQuestion } from "./active-question";
import { ConfirmStart } from "./confirm-start";
import { ConfirmSubmit } from "./confirm-submit";
import { QuestionNavigation } from "./question-navigation";
import { Action, State } from "./reducer";
import { QuestionCountByCategories } from "./types/test";
import { Content } from "./ui/content";
import { Sidebar } from "./ui/sidebar";
import { TestContainer } from "./ui/test-container";
import { TimerWrapper } from "./ui/timer-wrapper";

interface TestProps {
  /** Is this a practice test? */
  practice?: boolean;
  /** Is the test embedded inside a custom scroll parent? */
  embedded?: boolean;
  /** Anti-cheating measure to disable pasting for free text answers*/
  isAnswerPasteDisabled?: boolean;
  previousApplicationAt?: string | null;
  startedAt?: string | null;
  endsAt: Date | null;
  testType: TestType;
  questions?: PublicQuestion[];
  duration: number;
  questionCountByCategories: QuestionCountByCategories;
  startTest: () => void;
  startTestPending?: boolean;
  finishTest: () => void;
  state: State;
  dispatch: Dispatch<Action>;
}

export function Test({
  practice = false,
  embedded = false,
  state,
  dispatch,
  startedAt,
  endsAt,
  testType,
  questions,
  duration,
  isAnswerPasteDisabled,
  startTest,
  startTestPending,
  finishTest,
  questionCountByCategories,
  previousApplicationAt,
}: TestProps) {
  const [numberOfCopyDetected, setNumberOfCopyDetected] = useState(0);
  const [numberOfPasteDetected, setNumberOfPasteDetected] = useState(0);

  const reducerProps = { state, dispatch };
  const router = useRouter();
  const jobOpeningSlug = router.query.slug as string;
  const testSlug = router.query.hashedTestId as string;

  useScrollToTop(startedAt != null, state.activeQuestionIndex);

  const sortedQuestions = useMemo(
    () => orderBy(questions, [(question) => question.order_weight], ["asc"]),
    [questions],
  );

  const handleCopyEvent = () => {
    setNumberOfCopyDetected((prev) => prev + 1);
  };

  const handlePasteEvent = () => {
    setNumberOfPasteDetected((prev) => prev + 1);
  };

  return (
    <TestContainer testType={testType}>
      <TimerWrapper embedded={embedded} startedTest={startedAt != null}>
        <Timer
          onSubmit={finishTest}
          endsAt={endsAt}
          testDuration={duration}
          submitting={state.submitting}
          jobOpeningSlug={state.jobOpeningSlug}
        />
      </TimerWrapper>
      <Sidebar>
        {startedAt ? (
          <QuestionNavigation
            questions={sortedQuestions}
            testType={testType}
            {...reducerProps}
          />
        ) : null}
      </Sidebar>

      <Content startedTest={startedAt != null} testType={testType}>
        <QuestionWrapper>
          {startedAt == null ? (
            <ConfirmStart
              testType={testType}
              duration={duration}
              questionCount={
                questionCountByCategories.multipleChoice +
                questionCountByCategories.openEnded
              }
              startTest={startTest}
              startTestPending={startTestPending}
              jobOpeningSlug={jobOpeningSlug}
              testSlug={testSlug}
              questionCountByCategories={questionCountByCategories}
              previousApplicationAt={previousApplicationAt}
            />
          ) : (
            <ActiveQuestion
              question={sortedQuestions[state.activeQuestionIndex]}
              questions={sortedQuestions}
              endsAt={endsAt}
              isAnswerPasteDisabled={!!isAnswerPasteDisabled}
              onCopyDetected={handleCopyEvent}
              onPasteDetected={handlePasteEvent}
              {...reducerProps}
            />
          )}
        </QuestionWrapper>
      </Content>

      <LabelsWrapper>
        {numberOfCopyDetected > 0 && (
          <CopyPasteContent>
            <Trans>Copy detected</Trans>
            <CopyPasteNumber>{numberOfCopyDetected}</CopyPasteNumber>
          </CopyPasteContent>
        )}
        {numberOfPasteDetected > 0 && (
          <CopyPasteContent>
            <Trans>Paste detected</Trans>
            <CopyPasteNumber>{numberOfPasteDetected}</CopyPasteNumber>
          </CopyPasteContent>
        )}
        {practice && startedAt !== null && (
          <LeavePracticeMode>
            <Trans>Leave practice mode</Trans>
            <Close
              onClick={() => {
                Router.replace(router.asPath.replace("/practice", ""));
              }}
            >
              <Icon icon={faClose} color="purple-60" fontSize={12} />
            </Close>
          </LeavePracticeMode>
        )}
      </LabelsWrapper>

      <ConfirmSubmit
        onSubmit={finishTest}
        questions={sortedQuestions}
        testType={testType}
        skipSubmissionSummary={false}
        {...reducerProps}
      />
    </TestContainer>
  );
}

const LabelsWrapper = styled.div`
  position: absolute;
  top: 50px;
  right: 32px;
  width: 140px;
  display: none;

  @media (min-width: 1024px) {
    display: inline-block;
  }
`;

const LeavePracticeMode = styled.div`
  position: absolute;
  top: -10px;
  right: 8px;
  width: 154px;
  color: ${(props) => props.theme.colors.purple[100]};
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 12px;
  background: ${(props) => props.theme.colors.orange[50]};
  display: none;

  @media (min-width: 1024px) {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
`;

const Close = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
`;

const CopyPasteContent = styled.div`
  display: flex;
  align-items: center;
  width: auto;
  background: ${(props) => props.theme.colors.yellow[20]};
  padding: 3px 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => props.theme.colors.purple[100]};
  flex-direction: row;
  margin-bottom: 6px;
`;

const CopyPasteNumber = styled.div`
  min-width: 12px;
  height: 12px;
  font-size: 8px;
  font-weight: 600;
  border-radius: 50%;
  text-align: center;
  color: ${(props) => props.theme.colors.purple[100]};
  background: ${(props) => props.theme.colors.yellow[100]};
  margin-left: auto;
`;

const QuestionWrapper = styled.div`
  position: relative;
  height: 100%;
  z-index: 0;
`;
